import type { ContentSnapshot } from '../../engine/types';
import { ENGINE_VERSION } from '../../engine/engine';
import type { I18nPort } from '../../i18n/i18n-service';
import { configuredNetworkBaseUrl, NetworkApiClient } from '../../network/api-client';
import type { NetworkIdentity, NetworkRoomConfig, RoomSession } from '../../network/contracts';
import { loadNetworkIdentity, saveNetworkIdentity } from '../../network/identity-store';
import { WebRtcPeer, websocketUrl, type PeerChannel } from '../../network/webrtc-peer';
import { requireElement } from '../dom';
import type { LobbyController } from './lobby-controller';
import type { ToastController } from './toast-controller';
import type { AnalyticsClient } from '../../analytics/client';
import { battleRules } from '../battle-rules';

export interface NetworkReadyConnection {
  readonly identity: NetworkIdentity;
  readonly session: RoomSession;
  readonly config: NetworkRoomConfig;
  readonly peer: PeerChannel;
}

export interface NetworkSessionControllerOptions {
  readonly content: ContentSnapshot;
  readonly i18n: I18nPort;
  readonly lobby: LobbyController;
  readonly toast: ToastController;
  readonly onReady: (connection: NetworkReadyConnection) => void;
  readonly analytics: AnalyticsClient;
  readonly fastMode: boolean;
}

export class NetworkSessionController {
  private readonly createButton = requireElement<HTMLButtonElement>('#createRoomButton');
  private readonly joinButton = requireElement<HTMLButtonElement>('#joinRoomButton');
  private readonly cancelButton = requireElement<HTMLButtonElement>('#cancelNetworkButton');
  private readonly copyButton = requireElement<HTMLButtonElement>('#copyRoomCodeButton');
  private readonly acceptInviteButton = requireElement<HTMLButtonElement>('#acceptInviteButton');
  private readonly invitePreview = requireElement<HTMLElement>('#invitePreview');
  private readonly inviteHost = requireElement<HTMLElement>('#inviteHost');
  private readonly inviteRating = requireElement<HTMLElement>('#inviteRating');
  private readonly roomCode = requireElement<HTMLInputElement>('#roomCodeInput');
  private readonly status = requireElement<HTMLElement>('#networkStatus');
  private readonly leaderboard = requireElement<HTMLOListElement>('#networkLeaderboard');
  private readonly api = new NetworkApiClient(configuredNetworkBaseUrl());
  private abortController: AbortController | null = null;
  private peer: WebRtcPeer | null = null;
  private activeSession: RoomSession | null = null;
  private readonly inviteToken = inviteTokenFromPath(location.pathname);

  constructor(private readonly options: NetworkSessionControllerOptions) {
    this.createButton.addEventListener('click', () => { void this.createRoom(); });
    this.joinButton.addEventListener('click', () => { void this.joinRoom(); });
    this.cancelButton.addEventListener('click', () => { void this.cancel(true); });
    this.copyButton.addEventListener('click', () => { void this.copyCode(); });
    this.acceptInviteButton.addEventListener('click', () => { void this.joinInvite(); });
    this.options.i18n.subscribe(() => this.renderIdle());
  }

  boot(): void { void this.loadLeaderboard(); void this.loadInvite(); }

  private async cancel(showConfirmation = false): Promise<void> {
    const session = this.activeSession;
    this.abortController?.abort();
    this.abortController = null;
    this.peer?.close();
    this.peer = null;
    this.activeSession = null;
    this.setBusy(false);
    this.status.textContent = '';
    this.copyButton.hidden = true;
    if (session) {
      try { await this.api.abandonRoom(session); }
      catch { /* A replacement create also clears stale server state. */ }
    }
    if (showConfirmation) this.status.textContent = this.options.i18n.t('network.cancelled');
  }

  private async createRoom(): Promise<void> {
    this.options.lobby.commitPlayerName();
    await this.run(async (identity) => {
      const session = await this.api.createRoom(identity, {
        displayName: this.options.lobby.displayName,
        engineVersion: ENGINE_VERSION,
        contentVersion: this.options.content.version,
        arenaId: 'basecamp',
        rules: battleRules(this.options.fastMode),
        deck: this.options.lobby.decks.player,
      });
      this.roomCode.value = session.roomCode;
      this.status.textContent = this.options.i18n.t('network.roomCreated', { code: session.roomCode });
      this.copyButton.hidden = false;
      return session;
    });
  }

  private async joinInvite(): Promise<void> {
    if (!this.inviteToken) return;
    this.options.lobby.commitPlayerName();
    this.status.textContent = this.options.i18n.t('network.acceptingInvite');
    await this.run((identity) => this.api.joinInvite(identity, {
      inviteToken: this.inviteToken!,
      displayName: this.options.lobby.displayName,
      engineVersion: ENGINE_VERSION,
      contentVersion: this.options.content.version,
      deck: this.options.lobby.decks.player,
    }), true);
  }

  private async joinRoom(): Promise<void> {
    const roomCode = this.roomCode.value.trim().toUpperCase();
    if (!roomCode) return;
    this.options.lobby.commitPlayerName();
    this.status.textContent = this.options.i18n.t('network.joining', { code: roomCode });
    await this.run((identity) => this.api.joinRoom(identity, {
      roomCode,
      displayName: this.options.lobby.displayName,
      engineVersion: ENGINE_VERSION,
      contentVersion: this.options.content.version,
      deck: this.options.lobby.decks.player,
    }));
  }

  private async run(createSession: (identity: NetworkIdentity) => Promise<RoomSession>, consumedInvite = false): Promise<void> {
    await this.cancel();
    this.setBusy(true);
    const abortController = new AbortController();
    this.abortController = abortController;
    try {
      const identity = await this.api.ensureIdentity(this.options.lobby.displayName, loadNetworkIdentity(localStorage));
      saveNetworkIdentity(localStorage, identity);
      const session = await createSession(identity);
      this.activeSession = session;
      if (session.seat === 'seat-a') this.status.textContent = this.options.i18n.t('network.waiting');
      else this.status.textContent = this.options.i18n.t('network.connecting');
      if (consumedInvite) history.replaceState(null, '', `/${location.search}${location.hash}`);
      const ice = await this.api.iceServers(identity);
      const connectedSession = { ...session, websocketUrl: websocketUrl(configuredNetworkBaseUrl(), session) };
      const peer = new WebRtcPeer(connectedSession, ice.iceServers);
      this.peer = peer;
      const channel = await peer.connect(abortController.signal, () => {
        this.status.textContent = this.options.i18n.t('network.connecting');
      });
      this.options.analytics.event('network_transport', { relayAvailable: ice.relayAvailable, seat: session.seat, roundTripMs: channel.linkStats.roundTripMs, inputDelayTicks: channel.linkStats.inputDelayTicks });
      this.validateConfig(channel.config, session);
      this.status.textContent = this.options.i18n.t('network.ready');
      this.abortController = null;
      this.setBusy(false);
      this.options.onReady({ identity, session: connectedSession, config: channel.config, peer: channel });
    } catch (error) {
      if (abortController.signal.aborted) return;
      const message = error instanceof Error && error.message === 'peer-connection-timeout'
        ? this.options.i18n.t('network.connectionTimeout')
        : this.options.i18n.t('network.unavailable', { error: error instanceof Error ? error.message : String(error) });
      await this.cancel();
      this.status.textContent = message;
      this.options.toast.show(message, 8);
    }
  }

  private validateConfig(config: NetworkRoomConfig, session: RoomSession): void {
    if (config.networkMatchId !== session.networkMatchId) throw new Error('Room match identifier mismatch');
    if (config.engineVersion !== ENGINE_VERSION || config.contentVersion !== this.options.content.version) throw new Error('Opponent uses an incompatible game version');
    if (!this.options.content.arenas.has(config.arenaId as never)) throw new Error('Room uses an unknown arena');
    for (const deck of Object.values(config.decks)) {
      if (deck.length !== 8 || new Set(deck).size !== 8 || deck.some((cardId) => !this.options.content.cards.has(cardId))) throw new Error('Room contains an invalid deck');
    }
  }

  private async copyCode(): Promise<void> {
    try {
      const token = this.activeSession?.inviteToken;
      const value = token ? `${location.origin}/${token}` : this.activeSession?.roomCode ?? this.roomCode.value;
      await navigator.clipboard.writeText(value);
      this.options.toast.show(this.options.i18n.t(token ? 'network.inviteCopied' : 'network.codeCopied'));
    } catch {
      this.options.toast.show(this.options.i18n.t('network.copyFailed'));
    }
  }

  private async loadInvite(): Promise<void> {
    if (!this.inviteToken) return;
    try {
      const existing = loadNetworkIdentity(localStorage);
      const preview = await this.api.invitePreview(this.inviteToken, existing ?? undefined);
      this.inviteHost.textContent = this.options.i18n.t('network.invitedBy', { name: preview.hostDisplayName });
      this.inviteRating.textContent = preview.headToHead?.completed
        ? this.options.i18n.t('network.headToHead', { hostWins: preview.headToHead.hostWins, guestWins: preview.headToHead.guestWins, draws: preview.headToHead.draws })
        : this.options.i18n.t('network.hostRating', { wins: preview.hostRating.wins, games: preview.hostRating.completedMatches });
      this.invitePreview.hidden = false;
      this.roomCode.hidden = true;
      this.joinButton.hidden = true;
      this.createButton.hidden = true;
    } catch {
      this.status.textContent = this.options.i18n.t('network.inviteUnavailable');
    }
  }

  private async loadLeaderboard(): Promise<void> {
    try {
      const entries = await this.api.leaderboard();
      this.leaderboard.replaceChildren(...entries.map((entry) => {
        const item = document.createElement('li');
        item.textContent = this.options.i18n.t('network.rankLine', { rank: entry.rank, name: entry.displayName, wins: entry.wins, losses: entry.losses, draws: entry.draws });
        return item;
      }));
    } catch {
      this.leaderboard.replaceChildren();
    }
  }

  private setBusy(busy: boolean): void {
    this.createButton.disabled = busy;
    this.joinButton.disabled = busy;
    this.roomCode.disabled = busy;
    this.acceptInviteButton.disabled = busy;
    this.cancelButton.hidden = !busy;
  }

  private renderIdle(): void {
    if (!this.abortController) this.status.textContent = '';
  }
}

export function inviteTokenFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/([A-Za-z0-9_-]{8})\/?$/);
  return match?.[1] ?? null;
}
